const ENGRAVING_ROW_FONT_NAMES = ['mono', 'victor', 'cutive'];
const ENGRAVING_ROW_FONT_LABELS = {
  mono: 'Mono',
  victor: 'Victor',
  cutive: 'Cutive',
};
const ENGRAVING_ROW_FONT_FAMILIES = {
  mono: 'monospace',
  victor: '"Victor Mono", monospace',
  cutive: '"Cutive Mono", monospace',
};

class EngravingRow extends HTMLElement {
  static get observedAttributes() {
    return [
      'value',
      'quantity',
      'font-name',
      'placeholder',
      'max-quantity',
      'can-remove',
      'disabled',
      'error-message',
      'preview-label',
      'hide-font-selector',
      'font-label',
      'remove-label',
      'decrease-label',
      'increase-label',
      'mono-label',
      'victor-label',
      'cutive-label',
    ];
  }

  #initialized = false;
  #textInput = null;
  #quantityInput = null;
  #fontSelect = null;
  #previewButton = null;
  #removeButton = null;
  #errorElement = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    if (!this.#initialized) {
      this.#render();
      this.#bindEvents();
      this.#initialized = true;
    }

    this.#syncUiFromAttributes();
    this.autoResize();
  }

  attributeChangedCallback() {
    if (!this.#initialized) {
      return;
    }

    this.#syncUiFromAttributes();
  }

  get rowId() {
    return this.dataset.rowId || this.getAttribute('row-id') || '';
  }

  get value() {
    return this.#textInput?.value || this.getAttribute('value') || '';
  }

  set value(nextValue) {
    this.setAttribute('value', nextValue || '');
  }

  get quantity() {
    return this.#normalizeQuantity(this.#quantityInput?.value || this.getAttribute('quantity'));
  }

  set quantity(nextValue) {
    this.setAttribute('quantity', String(this.#normalizeQuantity(nextValue)));
  }

  get fontName() {
    return this.#normalizeFontName(this.#fontSelect?.value || this.getAttribute('font-name'));
  }

  set fontName(nextValue) {
    this.setAttribute('font-name', this.#normalizeFontName(nextValue));
  }

  get maxQuantity() {
    const parsedValue = parseInt(this.getAttribute('max-quantity') || '1', 10);
    return Number.isNaN(parsedValue) ? 1 : Math.max(1, parsedValue);
  }

  set maxQuantity(nextValue) {
    this.setAttribute('max-quantity', String(Math.max(1, parseInt(nextValue, 10) || 1)));
  }

  get canRemove() {
    return this.hasAttribute('can-remove');
  }

  set canRemove(nextValue) {
    this.toggleAttribute('can-remove', Boolean(nextValue));
  }

  get errorMessage() {
    return this.getAttribute('error-message') || '';
  }

  set errorMessage(nextValue) {
    if (nextValue) {
      this.setAttribute('error-message', nextValue);
    } else {
      this.removeAttribute('error-message');
    }
  }

  focusTextInput() {
    this.#textInput?.focus();
  }

  autoResize() {
    if (!this.#textInput) {
      return;
    }

    this.#textInput.style.height = 'auto';
    this.#textInput.style.height = `${this.#textInput.scrollHeight}px`;
  }

  #render() {
    const shadowRoot = /** @type {ShadowRoot} */ (this.shadowRoot);
    shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        .row {
          display: block;
        }

        .shell {
          position: relative;
          width: 100%;
        }

        .input {
          width: 100%;
          padding: 1rem;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
          font-family: inherit;
          transition: border-color 200ms ease;
          resize: none;
          box-sizing: border-box;
          overflow: hidden;
          margin-bottom: 0;
          min-height: 118px;
          padding-bottom: 3.25rem;
          line-height: 1.35;
        }

        .input:focus {
          outline: none;
          border-color: #000;
        }

        :host([error-message]) .input {
          border-color: #dc2626;
        }

        .controls {
          position: absolute;
          right: 0.875rem;
          bottom: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          z-index: 2;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .quantity-selector {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.125rem;
          border: 1px solid #d8d8d8;
          border-radius: 999px;
          overflow: hidden;
          min-height: 34px;
          padding: 0 0.25rem;
          background: rgba(255, 255, 255, 0.96);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .quantity-button {
          width: 22px;
          min-width: 22px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #000;
          border-radius: 999px;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
        }

        .quantity-icon {
          width: 12px;
          height: 12px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          line-height: 1;
        }

        .quantity-input {
          width: 20px;
          border: 0;
          text-align: center;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #000;
          padding: 0;
          background: transparent;
          appearance: textfield;
          -moz-appearance: textfield;
        }

        .quantity-input::-webkit-outer-spin-button,
        .quantity-input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .font-select {
          min-height: 34px;
          border: 1px solid #d8d8d8;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.96);
          padding: 0 0.875rem;
          font-size: 0.8125rem;
          font-weight: 600;
          color: #000;
          cursor: pointer;
          width: 84px;
        }

        :host([hide-font-selector]) .font-select {
          display: none;
        }

        .button {
          min-height: 34px;
          border-radius: 999px;
          border: 1px solid #cfcfcf;
          background: #fff;
          color: #222;
          padding: 0 0.875rem;
          font-size: 0.8125rem;
          font-weight: 600;
          cursor: pointer;
          transition: border-color 200ms ease, color 200ms ease, background-color 200ms ease, box-shadow 200ms ease, transform 200ms ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .button:hover,
        .quantity-button:hover,
        .font-select:hover {
          border-color: #000;
        }

        .preview-button,
        .remove-button {
          width: 36px;
          min-width: 36px;
          height: 36px;
          min-height: 36px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .preview-button svg,
        .remove-button svg {
          width: 18px;
          height: 18px;
        }

        .preview-button:hover {
          background: #f8f8f8;
          transform: translateY(-1px);
        }

        .remove-button:hover {
          border-color: #d92d20;
          color: #d92d20;
          background: #fff5f4;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
          transform: translateY(-1px);
        }

        .error {
          font-size: 0.875rem;
          color: #dc2626;
          background-color: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 0.75rem 1rem;
          margin: 0.5rem 0 0;
          text-align: left;
          font-weight: 500;
          line-height: 1.4;
          display: none;
        }

        :host([error-message]) .error {
          display: block;
        }

        :host([disabled]) .input,
        :host([disabled]) .font-select,
        :host([disabled]) .button,
        :host([disabled]) .quantity-button,
        :host([disabled]) .quantity-input {
          cursor: not-allowed;
        }

        :host([disabled]) .controls {
          opacity: 0.65;
        }

        @media screen and (max-width: 749px) {
          .controls {
            right: 0.75rem;
            bottom: 0.75rem;
            gap: 0.375rem;
          }

          .input {
            padding-bottom: 3.25rem;
          }
        }

        @media screen and (max-width: 350px) {
          .input {
            padding-bottom: 2.75rem;
          }

          .font-select {
            min-height: 28px;
            width: 60px;
            font-size: 0.6525rem;
          }

          .quantity-selector {
            min-height: 24px;
          }

          .quantity-button {
            width: 18px;
            min-width: 18px;
            height: 26px;
          }

          .preview-button, .remove-button {
            width: 28px;
            min-width: 28px;
            height: 28px;
            min-height: 28px;
          }

          .quantity-input {
            width: 18px;
            font-size: 0.6525rem;
            }

          
        }
      </style>

      <div class="row">
        <div class="shell">
          <textarea class="input" rows="2" autocomplete="off"></textarea>
          <div class="controls">
           <button type="button" class="button preview-button">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M9.5235 4.79973C6.76257 4.92905 4.08307 6.62063 1.1722 9.66543C0.993412 9.85244 0.993412 10.1474 1.1722 10.3344C4.08307 13.3793 6.76258 15.0709 9.52351 15.2003C12.2733 15.3291 15.2667 13.9138 18.8217 10.3399C19.0086 10.152 19.0086 9.84814 18.8217 9.6602C15.2667 6.0863 12.2733 4.67093 9.5235 4.79973ZM9.47509 3.7592C12.6521 3.61039 15.9149 5.26347 19.5564 8.92433C20.1479 9.5189 20.1479 10.4812 19.5564 11.0758C15.9149 14.7366 12.6521 16.3897 9.47508 16.2408C6.30917 16.0924 3.3912 14.1603 0.42305 11.0555C-0.141017 10.4655 -0.141017 9.53435 0.423051 8.94433C3.3912 5.8396 6.30918 3.90749 9.47509 3.7592Z" fill="currentColor"/>
                <path d="M13.5807 10.0002C13.5807 11.9741 11.9742 13.5586 10.012 13.5586C8.04979 13.5586 6.44327 11.9741 6.44327 10.0002C6.44327 8.02617 8.04979 6.44176 10.012 6.44176C11.9742 6.44176 13.5807 8.02617 13.5807 10.0002ZM10.012 12.5169C11.4096 12.5169 12.5426 11.3901 12.5426 10.0002C12.5426 8.6102 11.4096 7.48342 10.012 7.48342C8.61438 7.48342 7.48138 8.6102 7.48138 10.0002C7.48138 11.3901 8.61438 12.5169 10.012 12.5169Z" fill="currentColor"/>
              </svg>
            </button>
            <div class="quantity-selector">
              <button type="button" class="quantity-button" data-action="decrease">
                <span class="quantity-icon" aria-hidden="true">-</span>
              </button>
              <input type="number" class="quantity-input" min="1" step="1" inputmode="numeric">
              <button type="button" class="quantity-button" data-action="increase">
                <span class="quantity-icon" aria-hidden="true">+</span>
              </button>
            </div>
            <select class="font-select"></select>
            <button type="button" class="button remove-button">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <p class="error"></p>
      </div>
    `;

    this.#textInput = shadowRoot.querySelector('.input');
    this.#quantityInput = shadowRoot.querySelector('.quantity-input');
    this.#fontSelect = shadowRoot.querySelector('.font-select');
    this.#previewButton = shadowRoot.querySelector('.preview-button');
    this.#removeButton = shadowRoot.querySelector('.remove-button');
    this.#errorElement = shadowRoot.querySelector('.error');
  }

  #bindEvents() {
    this.#textInput?.addEventListener('input', () => {
      this.setAttribute('value', this.#textInput?.value || '');
      this.autoResize();
      this.#dispatchRowEvent('engraving-row-change');
    });

    this.#quantityInput?.addEventListener('input', () => {
      const nextQuantity = this.#normalizeQuantity(this.#quantityInput?.value);
      this.setAttribute('quantity', String(nextQuantity));
      this.#dispatchRowEvent('engraving-row-change');
    });

    this.#fontSelect?.addEventListener('change', () => {
      this.setAttribute('font-name', this.#normalizeFontName(this.#fontSelect?.value));
      this.#dispatchRowEvent('engraving-row-change');
    });

    this.shadowRoot?.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
      if (target) {
        event.preventDefault();
        const currentQuantity = this.quantity;
        const nextQuantity = target.getAttribute('data-action') === 'increase'
          ? currentQuantity + 1
          : currentQuantity - 1;
        this.setAttribute('quantity', String(this.#normalizeQuantity(nextQuantity)));
        this.#dispatchRowEvent('engraving-row-change');
        return;
      }

      if (event.target instanceof Element && event.target.closest('.preview-button')) {
        event.preventDefault();
        this.#dispatchRowEvent('engraving-row-preview');
        return;
      }

      if (event.target instanceof Element && event.target.closest('.remove-button')) {
        event.preventDefault();
        this.#dispatchRowEvent('engraving-row-remove');
      }
    });
  }

  #syncUiFromAttributes() {
    if (!this.#textInput || !this.#quantityInput || !this.#fontSelect || !this.#previewButton || !this.#removeButton || !this.#errorElement) {
      return;
    }

    const disabled = this.hasAttribute('disabled');
    const errorMessage = this.getAttribute('error-message') || '';
    const value = this.getAttribute('value') || '';

    if (this.#textInput.value !== value) {
      this.#textInput.value = value;
    }
    this.#textInput.placeholder = this.getAttribute('placeholder') || '';
    this.#textInput.disabled = disabled;

    const normalizedQuantity = this.#normalizeQuantity(this.getAttribute('quantity'));
    this.#quantityInput.min = '1';
    this.#quantityInput.max = String(this.maxQuantity);
    if (this.#quantityInput.value !== String(normalizedQuantity)) {
      this.#quantityInput.value = String(normalizedQuantity);
    }
    this.#quantityInput.disabled = disabled;

    this.#syncFontOptions();
    const normalizedFontName = this.#normalizeFontName(this.getAttribute('font-name'));
    if (this.#fontSelect.value !== normalizedFontName) {
      this.#fontSelect.value = normalizedFontName;
    }
    this.#fontSelect.style.fontFamily = ENGRAVING_ROW_FONT_FAMILIES[normalizedFontName] || ENGRAVING_ROW_FONT_FAMILIES.mono;
    this.#fontSelect.disabled = disabled;

    this.#previewButton.disabled = disabled;
    this.#previewButton.setAttribute('aria-label', this.getAttribute('preview-label') || 'Preview');

    this.#removeButton.setAttribute('aria-label', this.getAttribute('remove-label') || 'Remove engraving text');
    this.#removeButton.disabled = disabled || !this.hasAttribute('can-remove');

    const decreaseButton = this.shadowRoot?.querySelector('[data-action="decrease"]');
    const increaseButton = this.shadowRoot?.querySelector('[data-action="increase"]');
    if (decreaseButton instanceof HTMLButtonElement) {
      decreaseButton.disabled = disabled;
      decreaseButton.setAttribute('aria-label', this.getAttribute('decrease-label') || 'Decrease quantity');
    }

    if (increaseButton instanceof HTMLButtonElement) {
      increaseButton.disabled = disabled;
      increaseButton.setAttribute('aria-label', this.getAttribute('increase-label') || 'Increase quantity');
    }

    this.#errorElement.textContent = errorMessage;
    this.autoResize();
  }

  #syncFontOptions() {
    if (!this.#fontSelect) {
      return;
    }

    const labels = {
      mono: this.getAttribute('mono-label') || ENGRAVING_ROW_FONT_LABELS.mono,
      victor: this.getAttribute('victor-label') || ENGRAVING_ROW_FONT_LABELS.victor,
      cutive: this.getAttribute('cutive-label') || ENGRAVING_ROW_FONT_LABELS.cutive,
    };

    const nextOptionsMarkup = ENGRAVING_ROW_FONT_NAMES
      .map((fontName) => `<option value="${fontName}">${labels[fontName]}</option>`)
      .join('');

    if (this.#fontSelect.innerHTML !== nextOptionsMarkup) {
      this.#fontSelect.innerHTML = nextOptionsMarkup;
    }

    this.#fontSelect.setAttribute('aria-label', this.getAttribute('font-label') || 'Font');
  }
  #normalizeQuantity(value) {
    const parsedValue = parseInt(String(value || '1'), 10);
    const fallbackValue = Number.isNaN(parsedValue) ? 1 : parsedValue;
    return Math.max(1, Math.min(this.maxQuantity, fallbackValue));
  }

  #normalizeFontName(value) {
    return ENGRAVING_ROW_FONT_NAMES.includes(value) ? value : 'mono';
  }

  #dispatchRowEvent(name) {
    this.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail: {
        rowId: this.rowId,
        text: this.value.trim(),
        quantity: this.quantity,
        fontName: this.fontName,
      },
    }));
  }
}

if (!customElements.get('engraving-row')) {
  customElements.define('engraving-row', EngravingRow);
}
